/**
 * EnderecosEditor — os endereços titulados de um Lead ou Cliente (ADR-0071).
 *
 * Um cartão por endereço (`EnderecoFields`). O primeiro é o principal e leva
 * o selo; "Tornar principal" traz outro para o topo. No cliente
 * (`permitirCobranca`), cada cartão tem "Usar para cobrança" — marcar um
 * desmarca os outros, e sem nenhum marcado a cobrança usa o principal
 * (decisão do dono, 2026-09-15).
 *
 * Nada é salvo aqui: o editor só devolve a lista nova por `onChange`, e o
 * pai envia `prepararEnderecos(lista).enderecos` no POST/PATCH (cartão em
 * branco fica de fora — `utils/endereco`).
 *
 * Props:
 *   - value: lista de endereços (a do payload, `enderecos[]`).
 *   - onChange(lista): lista completa nova, na ordem (índice 0 = principal).
 *   - buscar?: repassado a cada `EnderecoFields` (busca de CEP).
 *   - permitirCobranca?: mostra "Usar para cobrança" (só Cliente).
 *   - somenteLeitura?: lista formatada, com título e selos Principal/Cobrança.
 *   - disabled?: desabilita campos e botões.
 *   - erros?: `enderecos` do 400 da API ou `validarCartoes`: um objeto por
 *     posição (`{}` = ok) ou mensagens da lista inteira (strings). As
 *     posições valem para a lista em que foram calculadas — ao reordenar, o
 *     pai limpa os erros.
 *   - idPrefix: prefixo único na página para os ids dos campos.
 *
 * Endereço novo criado fora do editor deve vir de `novoEndereco()`: a chave
 * local (`_chave`) mantém o cartão — e o foco — no lugar enquanto a pessoa
 * digita. Endereço do servidor usa o `id`. "Adicionar endereço" leva o foco
 * ao CEP do cartão novo, o primeiro campo.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiPlus, FiStar, FiTrash2 } from "react-icons/fi";

import Checkbox from "../Checkbox";
import EnderecoFields from "../EnderecoFields";
import { AvisoDoCampo } from "../EnderecoFields/styles";
import {
  AmbienteCard as CartaoEndereco,
  AmbienteNome as TituloDoCartao,
  ErrorMsg,
  FieldLabel,
  FieldRow,
  FieldValue,
  IconButton,
  InlineHint,
  Pill,
  SecondaryButton,
} from "../../pages/Orcamentos/styles";
import {
  MAX_ENDERECOS,
  enderecoVazio,
  formatarEndereco,
  indiceCobranca,
  marcarCobranca,
  remover,
  substituir,
  tornarPrincipal,
} from "../../utils/endereco";
import {
  AcoesDoCartao,
  CabecalhoDoCartao,
  LinhaDeCobranca,
  LinhaSomenteLeitura,
  ListaDeEnderecos,
  RodapeDaLista,
  Selos,
} from "./styles";

let ultimaChave = 0;

/** Endereço vazio com chave local estável — use para criar fora do editor. */
export function novoEndereco(titulo = "") {
  ultimaChave += 1;
  return { ...enderecoVazio(titulo), _chave: `novo-${ultimaChave}` };
}

function chaveDe(endereco, posicao) {
  return String(endereco?.id || endereco?._chave || `posicao-${posicao}`);
}

function tituloExibido(endereco, posicao) {
  return String(endereco?.titulo || "").trim() || `Endereço ${posicao + 1}`;
}

function errosDoItem(erros, posicao) {
  const erro = Array.isArray(erros) ? erros[posicao] : null;
  return erro && typeof erro === "object" ? erro : undefined;
}

function errosDaLista(erros) {
  return Array.isArray(erros) ? erros.filter((erro) => typeof erro === "string") : [];
}

function mensagemDe(erro) {
  if (!erro) return "";
  return Array.isArray(erro) ? erro.join(" ") : String(erro);
}

function ListaSomenteLeitura({ enderecos, permitirCobranca }) {
  if (enderecos.length === 0) {
    return <InlineHint>Nenhum endereço cadastrado.</InlineHint>;
  }
  const cobranca = permitirCobranca ? indiceCobranca(enderecos) : -1;
  return (
    <ListaDeEnderecos role="list">
      {enderecos.map((endereco, posicao) => (
        <FieldRow key={chaveDe(endereco, posicao)} role="listitem">
          <FieldLabel>{tituloExibido(endereco, posicao)}</FieldLabel>
          <FieldValue>
            <LinhaSomenteLeitura>
              <span>{formatarEndereco(endereco) || "—"}</span>
              <Selos>
                {posicao === 0 && <Pill $tone="info">Principal</Pill>}
                {posicao === cobranca && <Pill>Cobrança</Pill>}
              </Selos>
            </LinhaSomenteLeitura>
          </FieldValue>
        </FieldRow>
      ))}
    </ListaDeEnderecos>
  );
}

export default function EnderecosEditor({
  value,
  onChange,
  buscar,
  permitirCobranca = false,
  somenteLeitura = false,
  disabled = false,
  erros,
  idPrefix = "enderecos",
}) {
  const enderecos = Array.isArray(value) ? value : [];

  // Cada cartão avisa a mudança pela CHAVE, e a posição é procurada na lista
  // atual: a busca de CEP responde depois de um `await`, e nesse meio-tempo
  // a pessoa pode ter reordenado ou removido cartões.
  const listaRef = useRef(enderecos);
  listaRef.current = enderecos;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [focarCepDe, setFocarCepDe] = useState(null);

  useEffect(() => {
    if (!focarCepDe) return;
    setFocarCepDe(null);
    document.getElementById(`${idPrefix}-${focarCepDe}-cep`)?.focus();
  }, [focarCepDe, idPrefix]);

  if (somenteLeitura) {
    return <ListaSomenteLeitura enderecos={enderecos} permitirCobranca={permitirCobranca} />;
  }

  const emitir = (lista) => {
    listaRef.current = lista;
    if (onChangeRef.current) onChangeRef.current(lista);
  };
  const posicaoDe = (chave) =>
    listaRef.current.findIndex((endereco, posicao) => chaveDe(endereco, posicao) === chave);
  const aplicar = (chave, operacao) => {
    const posicao = posicaoDe(chave);
    if (posicao !== -1) emitir(operacao(listaRef.current, posicao));
  };

  const adicionar = () => {
    const novo = novoEndereco();
    emitir([...listaRef.current, novo]);
    setFocarCepDe(novo._chave);
  };

  const atingiuLimite = enderecos.length >= MAX_ENDERECOS;
  const semCobrancaMarcada = !enderecos.some((endereco) => endereco?.cobranca);

  return (
    <ListaDeEnderecos>
      {errosDaLista(erros).map((mensagem) => (
        <ErrorMsg key={mensagem} role="alert">
          {mensagem}
        </ErrorMsg>
      ))}

      {enderecos.length === 0 && <InlineHint>Nenhum endereço cadastrado.</InlineHint>}

      {enderecos.map((endereco, posicao) => {
        const chave = chaveDe(endereco, posicao);
        const prefixo = `${idPrefix}-${chave}`;
        const titulo = tituloExibido(endereco, posicao);
        const erro = errosDoItem(erros, posicao);
        const erroCobranca = mensagemDe(erro?.cobranca);
        const erroDeId = mensagemDe(erro?.id);
        const idErroCobranca = `${prefixo}-cobranca-erro`;
        return (
          <CartaoEndereco key={chave} aria-labelledby={`${prefixo}-cabecalho`}>
            <CabecalhoDoCartao>
              <TituloDoCartao id={`${prefixo}-cabecalho`}>{titulo}</TituloDoCartao>
              <Selos>
                {posicao === 0 && <Pill $tone="info">Principal</Pill>}
                {permitirCobranca && endereco?.cobranca && <Pill>Cobrança</Pill>}
              </Selos>
              <AcoesDoCartao>
                {posicao > 0 && (
                  <IconButton
                    type="button"
                    onClick={() => aplicar(chave, tornarPrincipal)}
                    disabled={disabled}
                    aria-label={`Tornar principal: ${titulo}`}
                  >
                    <FiStar aria-hidden="true" /> Tornar principal
                  </IconButton>
                )}
                <IconButton
                  type="button"
                  onClick={() => aplicar(chave, remover)}
                  disabled={disabled}
                  aria-label={`Remover ${titulo}`}
                >
                  <FiTrash2 aria-hidden="true" /> Remover
                </IconButton>
              </AcoesDoCartao>
            </CabecalhoDoCartao>

            {/* Endereço que não é deste cadastro não tem campo para apontar:
                o erro é do cartão inteiro e precisa ser anunciado. */}
            {erroDeId && (
              <AvisoDoCampo id={`${prefixo}-id-erro`} role="alert" $tom="danger">
                {erroDeId}
              </AvisoDoCampo>
            )}

            <EnderecoFields
              value={endereco}
              onChange={(novo) => aplicar(chave, (lista, indice) => substituir(lista, indice, novo))}
              buscar={buscar}
              disabled={disabled}
              erros={erro}
              idPrefix={prefixo}
            />

            {permitirCobranca && (
              <LinhaDeCobranca htmlFor={`${prefixo}-cobranca`}>
                <Checkbox
                  id={`${prefixo}-cobranca`}
                  checked={Boolean(endereco?.cobranca)}
                  onChange={(evento) =>
                    aplicar(chave, (lista, indice) => marcarCobranca(lista, indice, evento.target.checked))
                  }
                  disabled={disabled}
                  aria-invalid={erroCobranca ? true : undefined}
                  aria-describedby={erroCobranca ? idErroCobranca : undefined}
                />
                Usar para cobrança
              </LinhaDeCobranca>
            )}
            {erroCobranca && (
              <AvisoDoCampo id={idErroCobranca} $tom="danger">
                {erroCobranca}
              </AvisoDoCampo>
            )}
          </CartaoEndereco>
        );
      })}

      <RodapeDaLista>
        <SecondaryButton type="button" onClick={adicionar} disabled={disabled || atingiuLimite}>
          <FiPlus aria-hidden="true" /> Adicionar endereço
        </SecondaryButton>
        {atingiuLimite && <InlineHint>{`Limite de ${MAX_ENDERECOS} endereços.`}</InlineHint>}
        {permitirCobranca && enderecos.length > 0 && semCobrancaMarcada && (
          <InlineHint>Sem endereço de cobrança marcado, a cobrança usa o principal.</InlineHint>
        )}
      </RodapeDaLista>
    </ListaDeEnderecos>
  );
}
